<script lang="ts" context="module">
  import type { schema } from "@editor/schema";
  import { fade } from "svelte/transition";
  import { getNewsletterStores } from "../newsletterStores";
  import type { DesignStoreContent } from "../newsletterStores";
  import Background from "../rendering/Background.svelte";
  import { loadGlyphsForText, headers } from "../svgkit";
  import Block from "./Block.svelte";
  import { isEditor } from "./utils";
  import type { HeaderProps } from "../svgkit/rendering/headers/types";
  import Spinner from "@ui/Spinner.svelte";
  import { isRTL } from "sbelt/text";

  export const DEFAULT_AND_MIN_HEIGHT = 280;
  export const MAX_HEIGHT = 350;

  function headerHeight(b: schema.HeaderBlock, feelId: string) {
    if (b && b.custom) {
      return Math.min(Math.max(b.custom?.h || DEFAULT_AND_MIN_HEIGHT, DEFAULT_AND_MIN_HEIGHT), MAX_HEIGHT);
    }

    switch (feelId) {
      case "classic-legacy":
      case "handwritten":
      case "minimal":
      case "vintage":
      case "stars":
      case "pumpkin":
      case "summertime":
      case "modern":
      default:
        return DEFAULT_AND_MIN_HEIGHT;
    }
  }

  export function headerForDesign(b: schema.HeaderBlock, d: DesignStoreContent) {
    const { fontPack, color, feel } = d;
    const { title, subtitle, spacing } = fontPack.header;

    const rtl = isRTL(b.title);

    const props: HeaderProps = {
      spacing,
      height: headerHeight(b, d.feel.id),
      title: {
        color: color.header.title,
        stripe: color["text.title"].stripe,
        font: title.customFont,
        text: b.title,
        limits: { min: title.minSize, max: title.maxSize },
        letterSpacing: title.letterSpacing,
        lineHeight: title.lineHeight
      },
      sub: {
        color: color.header.subtitle,
        font: subtitle.customFont,
        text: b.subtitle || (isEditor() ? "Click to add subtitle..." : ""),
        limits: { min: subtitle.minSize, max: subtitle.maxSize },
        letterSpacing: subtitle.letterSpacing,
        lineHeight: subtitle.lineHeight
      },
      shadow: color.header.bg,
      rtl
    };

    switch (feel.id) {
      case "classic-legacy":
        return headers.classic(props);
      case "minimal":
        return headers.minimal(props);
      case "vintage":
        return headers.vintage(props);
      case "handwritten":
        return headers.handwritten(props);
      case "heartfelt":
        return headers.heartfelt(props);
      case "blossom":
        return headers.blossom(props);
      case "summertime":
        return headers.summertime(props);
      case "pumpkin":
        return headers.pumpkin(props);
      case "stars":
        return headers.stars(props);
      case "modern":
      default:
        return headers.modern(props);
    }
  }
</script>

<script lang="ts">
  const { design } = getNewsletterStores();

  export let block: schema.HeaderBlock;

  // We don't use 'async' function here, because the "#await" directive in
  // svelte will render the "result" part immedietly if encountered a non promised value
  // which we rely on for SSR, and for quick transition between fonts (without the transition effects)
  // "async" function automatically wrap *all* result types in promise, thus making it problematic
  // also - in SSR rendering mode, svelte will render *only* the loading state
  // (for the #await directive) if encoutering a promise
  function loadFontsForHeader(b: schema.HeaderBlock, d: DesignStoreContent) {
    const { title, subtitle } = d.fontPack.header;

    return loadGlyphsForText(`${b.title} ${b.subtitle}`, title.customFont, subtitle.customFont);
  }

  // Toggle rendering of text
  $: renderText = !block.custom || block.custom.showText;
</script>

<Block {block} on:click size="clear" preventDelete let:isEditing showEmpty>
  <svelte:fragment slot="background">
    <div class="ada-block absolute inset-0 hidden bg-black" />

    {#if !block.custom && $design?.feel.id !== "handwritten"}
      {#key $design.background.id}
        {#if $design.color.header.bg}
          <div
            transition:fade|local={{ duration: 75 }}
            class="ada-hidden absolute inset-0 overflow-hidden rounded-t transition-all duration-75"
            style="background-color: {$design.color.header.bg}" />
        {/if}

        <Background background={$design.background} location="header" />

        {#if $design.color.header.tint}
          <div
            transition:fade|local={{ duration: 75 }}
            class="ada-hidden absolute inset-0 overflow-hidden rounded-t transition-all duration-75"
            style="background: {$design.color.header.tint}" />
        {/if}
      {/key}
    {/if}
  </svelte:fragment>

  {#if isEditor()}
    <!-- Overlay -->
    <div class="block-overlay pointer-events-none absolute inset-0 rounded-t" />
  {/if}

  <!-- Render title / subtitle for accessibility and translation -->

  <div
    class="translate-pointer-events-auto translate-opacity-100 translate-rtl pointer-events-none absolute inset-0 flex flex-col items-center justify-center p-6 text-center opacity-0">
    <h1
      class="ada-text-white mb-2 rounded-md bg-black bg-opacity-60 px-4 py-3 text-2xl md:text-6xl"
      style="color: {$design.color.header.title}">
      {block.title}
    </h1>
    {#if block.subtitle}
      <h2
        class="ada-text-white rounded-md bg-black bg-opacity-60 px-4 py-1 text-base md:text-2xl"
        style="color: {$design.color.header.subtitle}">
        {block.subtitle}
      </h2>
    {/if}
  </div>

  {#if block.custom}
    <!-- custom background logic here -->
    <div class="flex w-full items-center justify-center">
      <img
        src={block.custom.url}
        alt="{block.title} {block.subtitle}"
        style="max-height:{MAX_HEIGHT}px;min-height:{DEFAULT_AND_MIN_HEIGHT}px;"
        class="pointer-events-none inset-0 rounded-t object-contain" />
    </div>
  {/if}

  <!-- Render SVG header -->
  <div
    class="inset-0 transition-all duration-150 ease-in-out"
    class:invisible={!renderText}
    class:absolute={block.custom}
    style="height: {isEditor() ? headerHeight(block, $design.feel.id) + 'px' : 'auto'}">
    {#await loadFontsForHeader(block, $design)}
      <div class="absolute inset-0 top-0 flex items-center justify-center" transition:fade|local={{ duration: 150 }}>
        <div class="rounded-full bg-white bg-opacity-25 p-5 text-14 text-white"><Spinner size={35} color="rgba(255,255,255,0.4)" /></div>
      </div>
    {:then r}
      <div
        class="translate-invisible ada-text-white"
        class:absolute={isEditor()}
        class:inset-0={isEditor()}
        transition:fade|local={{ duration: 150 }}>
        {@html headerForDesign(block, $design)}
      </div>
    {/await}
  </div>

  {#if isEditing}
    <div class="absolute inset-0 rounded-t bg-black bg-opacity-25" transition:fade={{ duration: 150 }} />
  {/if}
</Block>

<style>
  .block-overlay {
    background: #dcf4f1;
    opacity: 0.2;
  }

  .svg-container svg {
    max-width: 100%;
  }
</style>
